import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useActiveCompanyId } from "../../api/useActiveCompanyId";
import { OperationsDay } from "./operationsDay";

// Both the dashboard and the day page must wait for the worker, rather than
// treating the initial 202 response as a successfully checked, empty day.
export function useOperationsDay() {
    const { value: companyId, resolved } = useActiveCompanyId();
    const [snapshot, setSnapshot] = useState<{ companyId: string; data: OperationsDay } | null>(null);
    const [failure, setFailure] = useState<{ companyId: string; message: string } | null>(null);
    const [busy, setBusy] = useState<{ companyId: string | null; loading: boolean }>({ companyId: null, loading: true });
    const [revision, setRevision] = useState(0);
    const reload = useCallback(() => setRevision(value => value + 1), []);

    useEffect(() => {
        setSnapshot(current => current?.companyId === companyId && resolved ? current : null);
        setFailure(null);
        if (!resolved || !companyId) { setBusy({ companyId, loading: false }); return; }
        let alive = true;
        let request: AbortController | null = null;
        let running = false;
        let pendingTimer: number | null = null;
        const load = async (refresh = false) => {
            if (running) return;
            if (pendingTimer !== null) { window.clearTimeout(pendingTimer); pendingTimer = null; }
            running = true;
            let background = false;
            request = new AbortController();
            setBusy({ companyId, loading: true });
            try {
                const value = await api.get<OperationsDay>("/api/operations-day", refresh ? { refresh: 1 } : undefined, { signal: request.signal, timeout: 35000 });
                if (alive && !request.signal.aborted) {
                    if (String(value.companyId) !== companyId) { throw new Error("Получены данные другого салона. Обновите страницу."); }
                    background = value.cache?.refreshing === true;
                    setSnapshot(value.cache?.ready === false ? null : { companyId, data: value });
                    setFailure(value.cache?.refreshError ? { companyId, message: "Не удалось обновить данные. Попробуйте ещё раз." } : null);
                    if (background) { pendingTimer = window.setTimeout(() => void load(), 2000); }
                }
            } catch (err: any) {
                if (alive && !request.signal.aborted) {
                    setFailure({ companyId, message: err?.details?.message || err?.message || "Не удалось обновить данные. Попробуйте ещё раз." });
                    if ([401, 403].includes(Number(err?.status))) { setSnapshot(null); }
                }
            } finally { running = false; if (alive) setBusy({ companyId, loading: background }); }
        };
        void load(true);
        const check = () => { if (document.visibilityState === "visible") void load(); };
        const activate = () => { if (document.visibilityState === "visible") void load(true); };
        const timer = window.setInterval(check, 60000);
        document.addEventListener("visibilitychange", activate);
        window.addEventListener("focus", activate);
        return () => {
            alive = false; request?.abort(); window.clearInterval(timer);
            if (pendingTimer !== null) window.clearTimeout(pendingTimer);
            document.removeEventListener("visibilitychange", activate); window.removeEventListener("focus", activate);
        };
    }, [companyId, resolved, revision]);

    return {
        companyId, resolved,
        data: resolved && snapshot?.companyId === companyId ? snapshot.data : null,
        error: failure?.companyId === companyId ? failure.message : null,
        loading: !resolved || Boolean(companyId && (busy.companyId !== companyId || busy.loading)),
        reload,
    };
}
